import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Truck, Smartphone, Check, ArrowRight, ShieldCheck, Zap, X, Mail,
  MapPin, Fuel, BarChart3, Bot, LogIn, Accessibility,
} from "lucide-react";
import { API } from "../lib/api";

const SHOT_DASH = "/screens/dashboard.jpg";
const SHOT_VEHICLES = "/screens/vehicles.jpg";
const SHOT_MAP = "/screens/map.jpg";
const SHOT_DRIVERS = "/screens/drivers.jpg";
const ZEN_IMG =
  "https://images.unsplash.com/photo-1629494893504-d41e26a02631?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhbmRyb2lkJTIwYXBwJTIwbW9iaWxlJTIwcGhvbmUlMjBtb2NrdXB8ZW58MHx8fHwxNzg2MDY2MTY3fDA&ixlib=rb-4.1.0&q=85";
const PMR_IMG =
  "https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx3aGVlbGNoYWlyJTIwYWNjZXNzaWJsZSUyMHZhbiUyMHRyYW5zcG9ydHxlbnwwfHx8fDE3ODYxNDYxNDh8MA&ixlib=rb-4.1.0&q=85";

const MOBICARS_PLANS = [
  {
    id: "1user", name: "1 utilisateur", tagline: "Pour démarrer en solo", featured: false,
    monthly: { key: "mobicars_1user_monthly", price: "69", period: "€ / mois" },
    annual: { key: "mobicars_1user_annual", price: "690", period: "€ / an", note: "2 mois offerts" },
    features: ["1 utilisateur", "Suivi véhicules & contrôles", "Alertes email", "Assistant IA Mobi", "Cartes & géolocalisation"],
  },
  {
    id: "5users", name: "Forfait 5 utilisateurs", tagline: "Le plus populaire", featured: true,
    monthly: { key: "mobicars_5users_monthly", price: "329", period: "€ / mois" },
    annual: { key: "mobicars_5users_annual", price: "3290", period: "€ / an", note: "2 mois offerts" },
    features: ["Jusqu'à 5 utilisateurs", "Rôles & droits d'accès", "Import CSV & Excel", "Exports PDF & Excel", "Conducteurs & certifications", "Support prioritaire"],
  },
];

const MOBIPMR_PLANS = [
  {
    id: "pmr1user", name: "1 utilisateur", tagline: "Pour démarrer en solo", featured: false,
    monthly: { key: "mobipmr_1user_monthly", price: "69", period: "€ / mois" },
    annual: { key: "mobipmr_1user_annual", price: "690", period: "€ / an", note: "2 mois offerts" },
    features: ["1 utilisateur", "Transport adapté PMR", "Suivi des courses & trajets", "Alertes email", "Assistant IA intégré"],
  },
  {
    id: "pmr5users", name: "Forfait 5 utilisateurs", tagline: "Le plus populaire", featured: true,
    monthly: { key: "mobipmr_5users_monthly", price: "329", period: "€ / mois" },
    annual: { key: "mobipmr_5users_annual", price: "3290", period: "€ / an", note: "2 mois offerts" },
    features: ["Jusqu'à 5 utilisateurs", "Rôles & droits d'accès", "Gestion des accompagnants", "Exports PDF & Excel", "Conformité transport PMR", "Support prioritaire"],
  },
];

const APP_FEATURES = [
  { icon: ShieldCheck, label: "Contrôles techniques" },
  { icon: Fuel, label: "Suivi carburant & conso" },
  { icon: MapPin, label: "Géolocalisation flotte" },
  { icon: BarChart3, label: "Statistiques & budgets" },
  { icon: Bot, label: "Assistant IA intégré" },
  { icon: Zap, label: "Alertes automatiques" },
];

const ACCENTS = {
  indigo: {
    eyebrow: "text-zinc-500",
    toggleTag: "text-indigo-500",
    featuredCard: "bg-zinc-900 border-2 border-indigo-500 lg:scale-105 shadow-[0_0_40px_-10px_rgba(79,70,229,0.5)]",
    badge: "bg-indigo-600",
    check: "text-indigo-400",
    note: "text-indigo-300 bg-indigo-500/10 border-indigo-500/30",
    buyNormal: "bg-indigo-600 text-white hover:bg-indigo-700",
    buyFeatured: "bg-white text-zinc-950 hover:bg-zinc-200 hover:scale-[1.02]",
  },
  violet: {
    eyebrow: "text-violet-400",
    toggleTag: "text-violet-400",
    featuredCard: "bg-zinc-900 border-2 border-violet-500 lg:scale-105 shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)]",
    badge: "bg-violet-600",
    check: "text-violet-400",
    note: "text-violet-300 bg-violet-500/10 border-violet-500/30",
    buyNormal: "bg-violet-600 text-white hover:bg-violet-700",
    buyFeatured: "bg-white text-zinc-950 hover:bg-zinc-200 hover:scale-[1.02]",
  },
};

function PricingSection({ id, eyebrow, title, subtitle, plans, accentKey, productName, onBuy }) {
  const a = ACCENTS[accentKey];
  const [billing, setBilling] = useState("monthly");
  return (
    <section id={id} className="max-w-7xl mx-auto px-6 py-20 lg:py-24" data-testid={`pricing-${accentKey}`}>
      <div className="text-center mb-14">
        <span className={`text-xs uppercase tracking-[0.2em] font-bold ${a.eyebrow}`}>{eyebrow}</span>
        <h2 className="font-['Outfit'] font-bold text-3xl lg:text-5xl tracking-tight mt-3">{title}</h2>
        <p className="text-zinc-400 mt-4 max-w-xl mx-auto">{subtitle}</p>
        <div className="inline-flex items-center gap-1 mt-6 p-1 rounded-full border border-zinc-700 bg-zinc-900" data-testid={`billing-toggle-${accentKey}`}>
          <button
            data-testid={`toggle-monthly-${accentKey}`}
            onClick={() => setBilling("monthly")}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${billing === "monthly" ? "bg-white text-zinc-950" : "text-zinc-300"}`}
          >
            Mensuel
          </button>
          <button
            data-testid={`toggle-annual-${accentKey}`}
            onClick={() => setBilling("annual")}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${billing === "annual" ? "bg-white text-zinc-950" : "text-zinc-300"}`}
          >
            Annuel <span className={a.toggleTag}>−2 mois</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch max-w-4xl mx-auto">
        {plans.map((plan) => {
          const p = billing === "annual" ? plan.annual : plan.monthly;
          return (
            <div
              key={plan.id}
              data-testid={`plan-${p.key}`}
              className={`relative flex flex-col rounded-lg p-8 lg:p-10 ${plan.featured ? a.featuredCard : "bg-zinc-900/50 border border-zinc-800"}`}
            >
              {plan.featured && (
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 ${a.badge} text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded`}>
                  {plan.tagline}
                </span>
              )}
              <h3 className="font-['Outfit'] font-bold text-xl">{plan.name}</h3>
              {!plan.featured && <p className="text-zinc-500 text-sm mt-1">{plan.tagline}</p>}
              <div className="mt-6 flex items-baseline gap-1 font-['JetBrains_Mono']">
                <span className="text-5xl font-black text-white">{p.price}</span>
                <span className="text-zinc-400 font-semibold text-sm">{p.period}</span>
              </div>
              {p.note && <span className={`mt-2 inline-block w-fit text-xs font-bold border px-2 py-0.5 rounded ${a.note}`}>{p.note}</span>}
              <ul className="mt-8 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check size={18} className={`${a.check} shrink-0 mt-0.5`} strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`buy-${p.key}`}
                onClick={() => onBuy({ name: `${productName} — ${plan.name}`, ...p })}
                className={`mt-9 w-full inline-flex items-center justify-center gap-2 font-bold px-6 py-3.5 rounded-md transition-all ${plan.featured ? a.buyFeatured : a.buyNormal}`}
              >
                Acheter <ArrowRight size={18} strokeWidth={2.5} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [modalPlan, setModalPlan] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const openCheckout = (plan) => {
    setModalPlan(plan);
    setEmail("");
  };

  const startCheckout = async () => {
    const em = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(em)) {
      toast.error("Entrez un email valide");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lookup_key: modalPlan.key, quantity: 1, origin_url: window.location.origin, email: em }),
      });
      const j = await res.json();
      if (j.checkout_url) {
        window.location.href = j.checkout_url;
      } else {
        throw new Error("no url");
      }
    } catch (e) {
      console.error(e);
      toast.error("Impossible de démarrer le paiement. Réessayez.");
      setLoading(false);
    }
  };

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#09090b] text-white font-['Manrope'] selection:bg-indigo-600 selection:text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-md bg-indigo-600 flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(255,255,255,0.15)]">
              <Truck size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-['Outfit'] font-black text-lg tracking-tight">
              LEGENDARY<span className="text-indigo-500">-95</span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-zinc-300">
            <button data-testid="nav-logiciel" onClick={() => scrollTo("#produits")} className="hover:text-white transition-colors">Logiciel</button>
            <button data-testid="nav-tarifs" onClick={() => scrollTo("#tarifs")} className="hover:text-white transition-colors">Tarifs</button>
          </nav>
          <button
            data-testid="header-login"
            onClick={() => navigate("/login")}
            className="inline-flex items-center gap-2 border border-zinc-700 hover:bg-zinc-800 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors"
          >
            <LogIn size={16} strokeWidth={2.5} /> Connexion
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_0%,rgba(79,70,229,0.18),transparent)]" />
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid grid-cols-12 gap-12 items-center relative">
          <div className="col-span-12 lg:col-span-7">
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-indigo-400 font-bold mb-5">
              Logiciels & applications professionnels
            </span>
            <h1 className="font-['Outfit'] font-black text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
              Pilotez votre flotte<br />
              <span className="text-indigo-500">comme une légende.</span>
            </h1>
            <p className="text-lg lg:text-xl text-zinc-400 mt-6 max-w-xl leading-relaxed">
              Legendary-95 conçoit des outils métier fiables. Découvrez <strong className="text-white">MobicarsVSD</strong>,
              la plateforme de suivi de flotte tout-en-un, et <strong className="text-white">MobiPMR</strong>, le logiciel de transport adapté aux personnes à mobilité réduite.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <button
                data-testid="hero-cta-pricing"
                onClick={() => scrollTo("#tarifs")}
                className="inline-flex items-center gap-2 bg-white text-zinc-950 rounded-md hover:bg-zinc-200 px-7 py-3.5 font-bold hover:scale-[1.02] transition-transform"
              >
                Voir les offres <ArrowRight size={18} strokeWidth={2.5} />
              </button>
              <button
                data-testid="hero-cta-products"
                onClick={() => scrollTo("#produits")}
                className="inline-flex items-center gap-2 border border-zinc-700 text-white hover:bg-zinc-800 rounded-md px-7 py-3.5 font-semibold transition-colors"
              >
                Découvrir les logiciels
              </button>
            </div>
          </div>
          {/* Right promo panel */}
          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-indigo-600/20 blur-2xl rounded-2xl" />
              <div className="relative rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden shadow-[8px_8px_0px_0px_rgba(79,70,229,0.9)]">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-800 bg-zinc-950/60">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="w-3 h-3 rounded-full bg-amber-400" />
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="ml-3 text-xs font-['JetBrains_Mono'] text-zinc-500">MobicarsVSD — Tableau de bord</span>
                </div>
                <img src={SHOT_DASH} alt="Tableau de bord MobicarsVSD" className="w-full h-64 object-cover object-top" />
                <div className="p-4 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500 font-bold">En vedette</span>
                  <button data-testid="promo-open-app" onClick={() => navigate("/login")} className="text-sm font-bold text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1">
                    Ouvrir l'app <ArrowRight size={15} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Ribbon */}
        <div className="border-y border-white/10 bg-zinc-950/60 py-3 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">
            <span>Gestion de flotte</span><span className="text-indigo-500">•</span>
            <span>Transport PMR</span><span className="text-indigo-500">•</span>
            <span>Sécurité</span><span className="text-indigo-500">•</span>
            <span>Assistant IA</span><span className="text-indigo-500">•</span>
            <span>Exports PDF / Excel</span>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="produits" className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Nos logiciels</span>
        <h2 className="font-['Outfit'] font-bold text-3xl lg:text-5xl tracking-tight mt-3 mb-14">La suite Legendary-95</h2>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* MobicarsVSD — wide */}
          <div data-testid="product-mobicars" className="lg:col-span-2 group border border-zinc-800 bg-zinc-900/50 rounded-lg overflow-hidden hover:-translate-y-1 transition-transform duration-200">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 lg:p-10 flex flex-col">
                <div className="inline-flex items-center gap-2 text-indigo-400 mb-4">
                  <Truck size={22} strokeWidth={2.5} />
                  <span className="text-xs uppercase tracking-widest font-bold">Logiciel SaaS</span>
                </div>
                <h3 className="font-['Outfit'] font-bold text-2xl mb-3">MobicarsVSD</h3>
                <p className="text-zinc-400 leading-relaxed mb-6">
                  La plateforme complète de suivi de flotte : véhicules, contrôles techniques, vidanges, binômes,
                  carburant, géolocalisation, alertes et assistant IA.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-auto">
                  {APP_FEATURES.map((f) => (
                    <div key={f.label} className="flex items-center gap-2 text-sm text-zinc-300">
                      <f.icon size={16} className="text-indigo-400 shrink-0" strokeWidth={2.2} />
                      {f.label}
                    </div>
                  ))}
                </div>
                <button
                  data-testid="product-mobicars-cta"
                  onClick={() => scrollTo("#tarifs")}
                  className="mt-7 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-3 rounded-md transition-colors w-fit"
                >
                  Voir les tarifs <ArrowRight size={17} strokeWidth={2.5} />
                </button>
              </div>
              <div className="relative min-h-[240px] border-t md:border-t-0 md:border-l border-zinc-800">
                <img src={SHOT_VEHICLES} alt="MobicarsVSD — véhicules" className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>

          {/* MobiPMR — compact (violet) */}
          <div data-testid="product-mobipmr" className="group border border-zinc-800 bg-zinc-900/50 rounded-lg overflow-hidden hover:-translate-y-1 transition-transform duration-200 flex flex-col">
            <div className="relative h-52 bg-zinc-950">
              <img src={PMR_IMG} alt="MobiPMR — transport adapté PMR" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="p-8 flex flex-col flex-1">
              <div className="inline-flex items-center gap-2 text-violet-400 mb-4">
                <Accessibility size={22} strokeWidth={2.5} />
                <span className="text-xs uppercase tracking-widest font-bold">Logiciel SaaS</span>
              </div>
              <h3 className="font-['Outfit'] font-bold text-2xl mb-3">MobiPMR</h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Le logiciel de gestion du transport adapté aux personnes à mobilité réduite : courses, accompagnants, conformité et suivi.
              </p>
              <button
                data-testid="product-mobipmr-cta"
                onClick={() => scrollTo("#tarifs-mobipmr")}
                className="mt-auto inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-3 rounded-md transition-colors w-fit"
              >
                Voir les tarifs <ArrowRight size={17} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Easy Zen — compact */}
          <div data-testid="product-easyzen" className="group border border-zinc-800 bg-zinc-900/50 rounded-lg overflow-hidden hover:-translate-y-1 transition-transform duration-200 flex flex-col">
            <div className="relative h-52 bg-zinc-950">
              <img src={ZEN_IMG} alt="Easy Zen App Android" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="p-8 flex flex-col flex-1">
              <div className="inline-flex items-center gap-2 text-cyan-400 mb-4">
                <Smartphone size={22} strokeWidth={2.5} />
                <span className="text-xs uppercase tracking-widest font-bold">App Android</span>
              </div>
              <h3 className="font-['Outfit'] font-bold text-2xl mb-3">Easy Zen</h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                L'application mobile Android pour rester serein au quotidien. Simple, rapide, à portée de main.
              </p>
              <span data-testid="easyzen-badge" className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-zinc-400 border border-zinc-700 rounded-md px-4 py-2.5 w-fit">
                <Smartphone size={16} strokeWidth={2.2} /> Bientôt sur Android
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* App gallery — real MobicarsVSD screens */}
      <section id="apercu" className="max-w-7xl mx-auto px-6 pb-4 lg:pb-8" data-testid="app-gallery">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">En images</span>
          <h2 className="font-['Outfit'] font-bold text-3xl lg:text-5xl tracking-tight mt-3">L'application en un coup d'œil</h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">De vraies captures de MobicarsVSD : tableau de bord, véhicules, géolocalisation et conducteurs.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { src: SHOT_DASH, label: "Tableau de bord & alertes contrôle", slug: "dashboard" },
            { src: SHOT_VEHICLES, label: "Fiches véhicules & contrôles techniques", slug: "vehicles" },
            { src: SHOT_MAP, label: "Géolocalisation de la flotte", slug: "map" },
            { src: SHOT_DRIVERS, label: "Conducteurs & certifications", slug: "drivers" },
          ].map((s) => (
            <figure
              key={s.slug}
              data-testid={`shot-${s.slug}`}
              className="group rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden hover:-translate-y-1 transition-transform duration-200 shadow-[6px_6px_0px_0px_rgba(79,70,229,0.35)]"
            >
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-800 bg-zinc-950/60">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="ml-3 text-xs font-['JetBrains_Mono'] text-zinc-500 truncate">{s.label}</span>
              </div>
              <img src={s.src} alt={s.label} loading="lazy" className="w-full object-cover object-top opacity-90 group-hover:opacity-100 transition-opacity" />
            </figure>
          ))}
        </div>
      </section>

      {/* Pricing — MobicarsVSD */}
      <PricingSection
        id="tarifs"
        eyebrow="Tarifs MobicarsVSD"
        title="MobicarsVSD — Choisissez votre formule"
        subtitle="Un compte MobicarsVSD est créé automatiquement après votre paiement. Prix en euros HT, TVA calculée au paiement."
        plans={MOBICARS_PLANS}
        accentKey="indigo"
        productName="MobicarsVSD"
        onBuy={openCheckout}
      />

      {/* Pricing — MobiPMR */}
      <PricingSection
        id="tarifs-mobipmr"
        eyebrow="Tarifs MobiPMR"
        title="MobiPMR — Choisissez votre formule"
        subtitle="Un compte MobiPMR est créé automatiquement après votre paiement. Prix en euros HT, TVA calculée au paiement."
        plans={MOBIPMR_PLANS}
        accentKey="violet"
        productName="MobiPMR"
        onBuy={openCheckout}
      />

      {/* Footer */}
      <footer className="border-t border-white/10 bg-zinc-950/60">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-indigo-600 flex items-center justify-center">
              <Truck size={17} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-['Outfit'] font-black tracking-tight">LEGENDARY<span className="text-indigo-500">-95</span></span>
          </div>
          <p className="text-zinc-500 text-sm">© {new Date().getFullYear()} Legendary-95 — Tous droits réservés.</p>
          <div className="flex items-center gap-6 text-sm text-zinc-400 font-semibold">
            <button onClick={() => scrollTo("#produits")} className="hover:text-white transition-colors">Logiciel</button>
            <button onClick={() => scrollTo("#tarifs")} className="hover:text-white transition-colors">Tarifs</button>
            <button data-testid="footer-login" onClick={() => navigate("/login")} className="hover:text-white transition-colors">Connexion</button>
          </div>
        </div>
      </footer>

      {/* Checkout email modal */}
      {modalPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/70 backdrop-blur-sm" data-testid="checkout-modal">
          <div className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-lg p-8 relative">
            <button data-testid="close-modal" onClick={() => setModalPlan(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
              <X size={20} />
            </button>
            <h3 className="font-['Outfit'] font-bold text-2xl mb-1">Finaliser l'achat</h3>
            <p className="text-zinc-400 text-sm mb-6 font-['JetBrains_Mono']">
              {modalPlan.name} — {modalPlan.price}{modalPlan.period}
            </p>
            <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Votre email</label>
            <div className="mt-2 flex items-center gap-2 border border-zinc-700 rounded-md px-3 bg-zinc-950 focus-within:border-indigo-500 transition-colors">
              <Mail size={18} className="text-zinc-500" />
              <input
                data-testid="checkout-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && startCheckout()}
                placeholder="vous@entreprise.fr"
                className="flex-1 bg-transparent py-3 text-white outline-none placeholder:text-zinc-600"
              />
            </div>
            <p className="text-zinc-500 text-xs mt-2">Votre compte sera créé avec cet email.</p>
            <button
              data-testid="confirm-checkout"
              onClick={startCheckout}
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white text-zinc-950 font-bold px-6 py-3.5 rounded-md hover:bg-zinc-200 transition-colors disabled:opacity-60"
            >
              {loading ? "Redirection…" : <>Payer avec Stripe <ArrowRight size={18} strokeWidth={2.5} /></>}
            </button>
            <p className="text-zinc-600 text-[11px] text-center mt-3">Paiement sécurisé par Stripe. Carte test : 4242 4242 4242 4242</p>
          </div>
        </div>
      )}
    </div>
  );
}
